import { useState, useEffect, useRef } from "react";
import { IFilterSet } from "../types";
import { apiService } from "../services/api";

// Module-level cache persists across modal open/close within session
const filterCache: Record<string, IFilterSet[]> = {};

export function useProductFilters(category: string | null) {
  const [filterSets, setFilterSets] = useState<IFilterSet[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!category) {
      setFilterSets([]);
      return;
    }

    if (filterCache[category]) {
      setFilterSets(filterCache[category]);
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoadingFilters(true);
    setFilterError(null);

    apiService
      .getFilterItems(category)
      .then((data) => {
        if (!controller.signal.aborted) {
          filterCache[category] = data;
          setFilterSets(data);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setFilterError("Failed to load filters");
          console.error("Error loading filters:", err);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingFilters(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [category]);

  return { filterSets, isLoadingFilters, filterError };
}
